import { useEffect, useState } from "react"

import { Loader } from "lucide-react"
import { toast } from "sonner"

import { AgentRunnerHistoryButton } from "@components/buttons/AgentRunnerHistoryButton"

import { SearchableExecutionRunRef } from "@models/executions"

import { useWatchSearchResult } from "@plugins/providers/agent/AgentHistoryContext"

import { Input } from "@ui/input"
import { Label } from "@ui/label"

export function AgentRunnerHistoryPanel() {
    const { search, error, loading, runs } = useWatchSearchResult()

    const [filterValue, setFilterValue] = useState<string>("")

    const [viewMoreToday, setViewMoreToday] = useState(false)
    const [todayItems, setTodayItems] = useState<SearchableExecutionRunRef[]>([])
    const filteredTodayItems = todayItems.filter((item) => item.name.toLowerCase().includes(filterValue.toLowerCase()))

    const [viewMoreYesterday, setViewMoreYesterday] = useState(false)
    const [yesterdayItems, setYesterdayItems] = useState<SearchableExecutionRunRef[]>([])
    const filteredYesterdayItems = yesterdayItems.filter((item) => item.name.toLowerCase().includes(filterValue.toLowerCase()))

    const [viewMorePastWeek, setViewMorePastWeek] = useState(false)
    const [pastWeekItems, setPastWeekItems] = useState<SearchableExecutionRunRef[]>([])
    const filteredPastWeekItems = pastWeekItems.filter((item) => item.name.toLowerCase().includes(filterValue.toLowerCase()))

    const [viewMoreOlder, setViewMoreOlder] = useState(false)
    const [olderItems, setOlderItems] = useState<SearchableExecutionRunRef[]>([])
    const filteredOlderItems = olderItems.filter((item) => item.name.toLowerCase().includes(filterValue.toLowerCase()))

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        setFilterValue(e.target.value)
    }

    useEffect(() => {
        if (!error) {
            return
        }

        toast.error("Error loading conversations", {
            description: error?.message || "An unknown error occurred while loading conversations.",
            duration: 5000,
        })
    }, [error])

    useEffect(() => {
        setTodayItems(search.getTodayRuns(runs))
        setYesterdayItems(search.getYesterdayRuns(runs))
        setPastWeekItems(search.getPastWeekRuns(runs))
        setOlderItems(search.getOlderRuns(runs))
    }, [runs, search])

    useEffect(() => {
        if (runs.length) {
            return
        }
        search.search()
    }, [])

    return (
        <div className="flex flex-col h-full w-full">
            {loading && (
                <div className="w-full flex justify-center p-2">
                    <Loader className="animate-spin motion-safe:animate-[spin_3s_linear_infinite]" />
                </div>
            )}
            {(todayItems.length > 0 || yesterdayItems.length > 0 || pastWeekItems.length > 0) && (
                <div className="px-2 mb-1">
                    <Label>Search conversations</Label>
                    <Input type="text" placeholder="Filter conversation(s)" onChange={handleInputChange} />
                </div>
            )}
            {filteredTodayItems.length === 0 && filteredYesterdayItems.length === 0 && filteredPastWeekItems.length === 0 && filteredOlderItems.length === 0 && <span className="mt-2 px-2 text-sm text-muted-foreground">No conversation found.</span>}
            <div className="flex-1 flex-row overflow-auto">
                {filteredTodayItems.length > 0 && (
                    <div className="flex flex-col gap-1 p-2">
                        <div className="flex flex-row items-center gap-2">
                            <h3 className="text-sm font-semibold">Today</h3>
                            {filteredTodayItems.length > 7 && (
                                <h3 className="ml-0.5 text-xs text-muted-foreground hover:cursor-pointer hover:underline hover:text-gray-900" onClick={() => setViewMoreToday(!viewMoreToday)}>
                                    {viewMoreToday ? "view Less" : "view More"}
                                </h3>
                            )}
                        </div>
                        {filteredTodayItems.slice(0, viewMoreToday ? filteredTodayItems.length : 7).map((item) => (
                            <AgentRunnerHistoryButton key={item.run.id} searchableRun={item} updateRun={(runId, updatedRun) => search.updateRunById(runId, updatedRun)} />
                        ))}
                    </div>
                )}
                {filteredYesterdayItems.length > 0 && (
                    <div className="flex flex-col gap-1 p-2">
                        <div className="flex flex-row items-center gap-2">
                            <h3 className="text-sm font-semibold">Yesterday</h3>
                            {filteredYesterdayItems.length > 7 && (
                                <h3 className="ml-0.5 text-xs text-muted-foreground hover:cursor-pointer hover:underline hover:text-gray-900" onClick={() => setViewMoreYesterday(!viewMoreYesterday)}>
                                    {viewMoreYesterday ? "view Less" : "view More"}
                                </h3>
                            )}
                        </div>
                        {filteredYesterdayItems.slice(0, viewMoreYesterday ? filteredYesterdayItems.length : 7).map((item) => (
                            <AgentRunnerHistoryButton key={item.run.id} searchableRun={item} updateRun={(runId, updatedRun) => search.updateRunById(runId, updatedRun)} />
                        ))}
                    </div>
                )}
                {filteredPastWeekItems.length > 0 && (
                    <div className="flex flex-col gap-2 p-2">
                        <div className="flex flex-row items-center gap-2">
                            <h3 className="text-sm font-semibold">Past Week</h3>
                            {filteredPastWeekItems.length > 7 && (
                                <h3 className="ml-0.5 text-xs text-muted-foreground hover:cursor-pointer hover:underline hover:text-gray-900" onClick={() => setViewMorePastWeek(!viewMorePastWeek)}>
                                    {viewMorePastWeek ? "view Less" : "view More"}
                                </h3>
                            )}
                        </div>
                        {filteredPastWeekItems.slice(0, viewMorePastWeek ? filteredPastWeekItems.length : 7).map((item) => (
                            <AgentRunnerHistoryButton key={item.run.id} searchableRun={item} updateRun={(runId, updatedRun) => search.updateRunById(runId, updatedRun)} />
                        ))}
                    </div>
                )}
                {filteredOlderItems.length > 0 && (
                    <div className="flex flex-col gap-2 p-2">
                        <div className="flex flex-row items-center gap-2">
                            <h3 className="text-sm font-semibold">Older</h3>
                            {filteredOlderItems.length > 7 && (
                                <h3 className="ml-0.5 text-xs text-muted-foreground hover:cursor-pointer hover:underline hover:text-gray-900" onClick={() => setViewMoreOlder(!viewMoreOlder)}>
                                    {viewMoreOlder ? "view Less" : "view More"}
                                </h3>
                            )}
                        </div>
                        {filteredOlderItems.slice(0, viewMoreOlder ? filteredOlderItems.length : 7).map((item) => (
                            <AgentRunnerHistoryButton key={item.run.id} searchableRun={item} updateRun={(runId, updatedRun) => search.updateRunById(runId, updatedRun)} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
